import { useNotification } from "@zapier/utils";

export interface UseCopyToClipboardArguments {
  notificationMessage?: React.ReactNode;
  showNotification?: boolean;
}

export type UseCopyToClipboardReturn = (
  value: string,
  options?: UseCopyToClipboardArguments,
) => Promise<void>;

/**
 * Copies a string to the clipboard and optionally shows a notification.
 */
export function useCopyToClipboard(
  baseOptions: UseCopyToClipboardArguments = {},
): UseCopyToClipboardReturn {
  const { createNotification } = useNotification();

  return async (
    value: string,
    overrideOptions: UseCopyToClipboardArguments = {},
  ) => {
    const options: Required<UseCopyToClipboardArguments> = {
      notificationMessage: "Copied to clipboard",
      showNotification: true,
      ...baseOptions,
      ...overrideOptions,
    };

    try {
      await navigator.clipboard.writeText(value);

      if (options.showNotification) {
        createNotification({
          type: "success",
          message: options.notificationMessage,
        });
      }
    } catch (error) {
      console.error("Failed to copy to clipboard", error);

      if (options.showNotification) {
        createNotification({
          type: "error",
          message: `Failed to copy to clipboard: ${
            error instanceof Error ? error.message : String(error)
          }`,
        });
      }
    }
  };
}
